import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';

const NAV_ITEMS = [
  { label: 'Overview', path: '/admin/dashboard' },
  { label: 'Media', path: '/admin/media' },
  { label: 'Device Setup', path: '/admin/setup' },
  { label: 'Users', path: '/admin/users' },
  { label: 'Packages', path: '/admin/packages' },
  { label: 'Map', path: '/admin/maps' },
  { label: 'Database & Services', path: '/admin/database' },
  { label: 'Inspection Registration', path: '/admin/inspection-registration' },
  { label: 'Inspection', path: '/admin/inspection' },
];

const AdminNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const currentPath = location.pathname;

  const handleSignOut = () => {
    localStorage.clear();
    navigate('/landing');
  };

  const isActive = (path) => currentPath === path;

  const goTo = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const navButtonSx = (path) => ({
    backgroundColor: isActive(path) ? 'rgba(76, 175, 80, 0.8)' : 'transparent',
    color: isActive(path) ? '#ffffff' : '#000',
    whiteSpace: 'nowrap',
    minHeight: 40,
    px: 1.5,
    '&:hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(76, 175, 80, 0.8)',
    },
  });

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: '#ececec',
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            backgroundColor: '#ececec',
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1, sm: 2 },
            gap: 1,
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              aria-label="Open menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ color: '#111', mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {isMobile ? (
            <Typography
              variant="subtitle1"
              sx={{
                flexGrow: 1,
                color: '#111',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {NAV_ITEMS.find((item) => isActive(item.path))?.label || 'Admin'}
            </Typography>
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { height: 4 },
              }}
            >
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  onClick={() => goTo(item.path)}
                  sx={navButtonSx(item.path)}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          <IconButton
            color="inherit"
            aria-label="Sign out"
            onClick={handleSignOut}
            sx={{
              color: '#111',
              '&:hover': {
                color: '#F44336',
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
              },
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 'min(320px, 86vw)',
            backgroundColor: '#f7f7f7',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, color: '#111' }}>
            Menu
          </Typography>
          <IconButton aria-label="Close menu" onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ py: 1 }}>
          {NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.path}
              selected={isActive(item.path)}
              onClick={() => goTo(item.path)}
              sx={{
                minHeight: 52,
                mx: 1,
                mb: 0.5,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(76, 175, 80, 0.85)',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.95)' },
                },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: isActive(item.path) ? 700 : 500 }}
              />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <List>
          <ListItemButton
            onClick={handleSignOut}
            sx={{ minHeight: 52, mx: 1, borderRadius: 1, color: '#c62828' }}
          >
            <LogoutIcon sx={{ mr: 1.5 }} />
            <ListItemText primary="Sign Out" />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
};

export default AdminNavbar;
